/* MOKAVELA: progressive, scroll-linked editorial motion. No commerce or analytics hooks. */
(() => {
  'use strict';

  const runtimeKey = '__mokavelaLuxuryMotion';
  window[runtimeKey]?.destroy();
  if (!document.body.classList.contains('mok-luxury')) return;

  const controller = new AbortController();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const revealSelector = '[data-mok-reveal]';
  const motionSelector = '[data-mok-steam], [data-mok-parallax], [data-mok-scroll-type]';
  const reveals = new Set();
  const moving = new Map();
  const visible = new Set();
  let revealObserver;
  let motionObserver;
  let frame = 0;
  let disposed = false;

  const listen = (target, type, callback, options = {}) => {
    target.addEventListener(type, callback, { ...options, signal: controller.signal });
  };
  const collect = (root, selector) => {
    const nodes = Array.from(root.querySelectorAll(selector));
    if (root.matches?.(selector)) nodes.unshift(root);
    return nodes;
  };
  const clamp = value => Math.min(1, Math.max(0, value));
  const reveal = element => {
    element.dataset.mokRevealState = 'visible';
    revealObserver?.unobserve(element);
  };
  const resetMotion = element => {
    element.style.removeProperty('--mok-parallax-y');
    element.style.removeProperty('--mok-scroll-x');
    element.querySelectorAll('[data-mok-steam-path]').forEach(path => {
      path.style.removeProperty('stroke-dasharray');
      path.style.removeProperty('stroke-dashoffset');
    });
  };
  const failOpen = () => {
    revealObserver?.disconnect();
    motionObserver?.disconnect();
    visible.clear();
    reveals.forEach(reveal);
    moving.forEach((_, element) => resetMotion(element));
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  };

  const paint = () => {
    frame = 0;
    if (disposed || reduced.matches || document.hidden) return;
    const height = window.innerHeight;
    const narrow = window.innerWidth < 750;
    // Collect geometry before writing styles to avoid interleaved layout reads/writes.
    const updates = [];
    visible.forEach(element => {
      const item = moving.get(element);
      if (!item || !element.isConnected) return;
      const rect = item.anchor.getBoundingClientRect();
      const progress = clamp((height - rect.top) / (height + rect.height));
      updates.push({ element, item, rect, progress });
    });
    updates.forEach(({ element, item, rect, progress }) => {
      if (item.paths.length) {
        const drawn = clamp((height * 0.92 - rect.top) / (height * 0.75 + rect.height * 0.45));
        item.paths.forEach(path => {
          path.style.strokeDasharray = '1';
          path.style.strokeDashoffset = (1 - drawn).toFixed(3);
        });
      }
      if (item.parallax) {
        element.style.setProperty('--mok-parallax-y', `${((progress - 0.5) * (narrow ? 8 : 20)).toFixed(2)}px`);
      }
      if (item.type) {
        element.style.setProperty('--mok-scroll-x', `${((0.5 - progress) * (narrow ? 24 : 64)).toFixed(2)}px`);
      }
    });
  };
  const schedule = () => {
    if (frame || disposed || reduced.matches || document.hidden || visible.size === 0) return;
    frame = requestAnimationFrame(paint);
  };

  const initializeObservers = () => {
    if (!('IntersectionObserver' in window)) return false;
    revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) reveal(entry.target);
      });
    }, { threshold: 0, rootMargin: '0px 0px -24px 0px' });
    motionObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      });
      schedule();
    }, { threshold: 0, rootMargin: '128px 0px' });
    return true;
  };

  const add = root => {
    collect(root, revealSelector).forEach(element => {
      if (reveals.has(element)) return;
      reveals.add(element);
      const rect = element.getBoundingClientRect();
      // The first screen, including the hero and its LCP image, is never held back.
      if (reduced.matches || !revealObserver || rect.top < window.innerHeight) {
        reveal(element);
        return;
      }
      revealObserver.observe(element);
      element.dataset.mokRevealState = 'waiting';
    });
    collect(root, motionSelector).forEach(element => {
      if (moving.has(element)) return;
      const parallax = element.hasAttribute('data-mok-parallax');
      const type = element.hasAttribute('data-mok-scroll-type');
      moving.set(element, {
        parallax,
        type,
        paths: Array.from(element.querySelectorAll('[data-mok-steam-path]')),
        // Read an untransformed parent for translated elements: no feedback drift.
        anchor: (parallax || type) && element.parentElement ? element.parentElement : element,
      });
      if (!reduced.matches) motionObserver?.observe(element);
    });
    schedule();
  };
  const remove = root => {
    reveals.forEach(element => {
      if (root === element || root.contains(element)) {
        revealObserver?.unobserve(element);
        delete element.dataset.mokRevealState;
        reveals.delete(element);
      }
    });
    moving.forEach((_, element) => {
      if (root === element || root.contains(element)) {
        motionObserver?.unobserve(element);
        visible.delete(element);
        resetMotion(element);
        moving.delete(element);
      }
    });
    if (visible.size === 0 && frame) {
      cancelAnimationFrame(frame);
      frame = 0;
    }
  };
  const preferenceChanged = () => {
    if (reduced.matches) {
      failOpen();
    } else {
      // Content revealed under reduced motion remains visible; only decoration resumes.
      moving.forEach((_, element) => motionObserver?.observe(element));
      schedule();
    }
  };
  const destroy = () => {
    disposed = true;
    failOpen();
    controller.abort();
    if (!reduced.addEventListener) reduced.removeListener(preferenceChanged);
    reveals.forEach(element => { delete element.dataset.mokRevealState; });
    reveals.clear();
    moving.clear();
    if (window[runtimeKey]?.destroy === destroy) delete window[runtimeKey];
  };

  window[runtimeKey] = { destroy };
  try {
    initializeObservers();
    add(document);
    listen(window, 'scroll', schedule, { passive: true });
    listen(window, 'resize', schedule, { passive: true });
    listen(document, 'visibilitychange', () => {
      if (document.hidden) {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
      } else schedule();
    });
    listen(document, 'focusin', event => {
      // Keyboard users must never arrive at a visually hidden focused descendant.
      reveals.forEach(element => {
        if (element.contains(event.target)) reveal(element);
      });
    });
    listen(document, 'shopify:section:load', event => {
      try { add(event.target); } catch (_) { failOpen(); }
    });
    listen(document, 'shopify:section:unload', event => remove(event.target));
    if (reduced.addEventListener) listen(reduced, 'change', preferenceChanged);
    else reduced.addListener(preferenceChanged);
    if (reduced.matches) failOpen();
  } catch (_) {
    destroy();
  }
})();
